import type { GooglePlace, OfferingCategory } from "@albertine/types";
import type {
    CatalogItemFull,
    CreateBookingDetails,
    RecommendationItem,
} from "@albertine/types/openapi/lmt";
import type { CreateOneOffBookingInput } from "../../EventEmitter";
import { placeTypesToOfferingCategories } from "../../types/GooglePlaces";
import InlineTextDelimiter from "../Common/InlineTextDelimiter";
import { AddToRequestMenu } from "./AddToRequestMenu";
import { useWebPlaceDetails } from "./hooks/useWebPlaceDetails";
import { WebItemView } from "./WebItemView";

type WebRecommendationItem = Extract<RecommendationItem, { source: "web" }>;

/** Categories that are rarely Google Places establishments — skip Places lookup entirely. */
const CATEGORIES_WITHOUT_PLACE_LOOKUP = new Set<OfferingCategory>([
    "event",
    "flight",
    "train",
    "travelItinerary",
]);

type WebItemDetailProps = {
    item: WebRecommendationItem;
    onAddToCatalog: (prefill?: {
        placeId?: string;
        initialValues?: Partial<CatalogItemFull>;
    }) => void;
    onAddOneOffBooking: (input: CreateOneOffBookingInput) => void;
};

export function WebItemDetail(props: WebItemDetailProps) {
    const { item, onAddToCatalog, onAddOneOffBooking } = props;

    const skipPlaceLookup = CATEGORIES_WITHOUT_PLACE_LOOKUP.has(item.category);
    const { data: fetchedPlace, isLoading: isLoadingWebPlace } = useWebPlaceDetails(
        skipPlaceLookup ? undefined : (item.placeId ?? undefined),
        skipPlaceLookup || item.placeId ? undefined : buildPlaceSearchText(item),
    );
    const webPlace =
        fetchedPlace && matchesCategory(fetchedPlace, item.category)
            ? fetchedPlace
            : undefined;

    const cityLabel = item.city?.trim() || undefined;

    return (
        <>
            <div className="catalog__item-actions flex w-full items-center justify-between">
                <h3>
                    {item.name ?? "..."}
                    {cityLabel ? (
                        <span className="text-muted-foreground">
                            <InlineTextDelimiter />
                            {cityLabel}
                        </span>
                    ) : null}
                </h3>
                <AddToRequestMenu
                    disabled={isLoadingWebPlace}
                    onAddToCatalog={() => {
                        onAddToCatalog({
                            placeId: webPlace?.id,
                            initialValues: {
                                description: item.description ?? undefined,
                                offeringCategories: [item.category],
                            },
                        });
                    }}
                    onAddOneOffBooking={() => {
                        onAddOneOffBooking(buildOneOffBookingInput(item, webPlace));
                    }}
                />
            </div>
            <WebItemView
                className="catalog__item"
                item={item}
                place={webPlace}
                isLoadingPlace={isLoadingWebPlace}
            />
        </>
    );
}

function buildOneOffBookingInput(
    item: WebRecommendationItem,
    place: GooglePlace | undefined,
): CreateOneOffBookingInput {
    const details: CreateBookingDetails = {
        name: item.name,
        description: item.description,
        address: place?.formattedAddress ?? item.address,
        country: item.country,
        googleUrl: place?.googleMapsUri ?? item.googleUrl,
        website: place?.websiteUri?.split("?")[0],
        phoneNumber: place?.internationalPhoneNumber,
        category: item.category,
        geoPoint: place?.location
            ? {
                  latitude: place.location.latitude,
                  longitude: place.location.longitude,
              }
            : undefined,
    };

    const photoUrls = (place?.photos ?? [])
        .map((photo) => photo.photoUri)
        .filter((url): url is string => !!url);

    return {
        details,
        photoUrls,
        recommendation: { optionId: item.id, fitScore: item.score },
    };
}

function buildPlaceSearchText(item: WebRecommendationItem): string {
    return [item.name, item.address, item.city, item.country]
        .filter(Boolean)
        .join(", ");
}

function matchesCategory(place: GooglePlace, category: OfferingCategory): boolean {
    const mappedCategories = placeTypesToOfferingCategories(place.types ?? []);
    if (mappedCategories.length === 0) {
        return true;
    }
    return mappedCategories.includes(category);
}
